import { Injectable } from '@angular/core';
import * as firebase from 'firebase';
import { AngularFireDatabase } from 'angularfire2/database';
import 'rxjs/add/operator/take';
import { ENV } from '@app/env';
import { Observable } from 'rxjs/Observable';
import { ICommercialSample } from '../models/commercial_sample.model';
import { AngularFirestore } from 'angularfire2/firestore';
import { switchMap } from 'rxjs/operators';
import { HistoryData } from './history';
import { TrackingData, _TRACKING_ENTITIES } from './tracking';
import { UtilitiesProvider } from './utilities';

@Injectable()
export class CommercialSamplesData {

  private action: 'update' = "update";

  constructor(
    private afdb: AngularFireDatabase,
    private afStore: AngularFirestore,
    private historyData: HistoryData,
    private tracking: TrackingData,
    private utilities: UtilitiesProvider
  ) { }

  /**
   * Get the active commercial samples
   * @param eventId
   */
  getActiveEventCommercialSamples(eventId: string): Observable<ICommercialSample[]> {
    return this.afdb.list<ICommercialSample>(`Clients/${ENV.client}/Events/${eventId}/CommercialSamples`, ref => {
      return ref.orderByChild('state').equalTo(true);
    }).valueChanges().map((data) => {
      return data;
    });
  }

  getActiveClientCommercialSamples(): Observable<ICommercialSample[]> {
    return this.afdb.list<ICommercialSample>(`Clients/${ENV.client}/CommercialSamples`, ref => {
      return ref.orderByChild('state').equalTo(true);
    }).valueChanges().map((data) => {
      return data;
    });
  }

  getCommercialSampleById(sampleId: string): Observable<ICommercialSample> {
    return this.afdb.object<ICommercialSample>(`/Clients/${ENV.client}/CommercialSamples/${sampleId}`).valueChanges();
  }

  getComercialSampleRef(sampleId: string) {
    return this.afdb.object<ICommercialSample>(`/Clients/${ENV.client}/CommercialSamples/${sampleId}`).query.ref;
  }

  getEventCommercialSampleById(eventId: string, sampleId: string): Observable<ICommercialSample> {
    return this.afdb.object<ICommercialSample>(`/Clients/${ENV.client}/Events/${eventId}/CommercialSamples/${sampleId}`).valueChanges();
  }

  updateSampleById(sampleId: string, sampleForm: any) {
    return this.afdb.object(`/Clients/${ENV.client}/CommercialSamples/${sampleId}/`).update({
      name: { "es": sampleForm.nameEs, "en": sampleForm.nameEn },
      description: { "es": sampleForm.descriptionEs, "en": sampleForm.descriptionEn },
      zone: sampleForm.zone,
      contact: { "phone": sampleForm.phone, "email": sampleForm.email, "name": sampleForm.inCharge },
    });
  }

  updateImageSample(sampleId: string, image: any) {
    return this.afdb.object(`/Clients/${ENV.client}/CommercialSamples/${sampleId}/`).update({
      photos: { "0": image }
    });
  }

  public async updateComercialSampleById(client: string, stand: ICommercialSample, photos: { data: string | ArrayBuffer, new: boolean, file?: File }[]) {
    this.action = "update";
    stand.update_at = new Date().getTime()
    const imageLoad = photos.map((p, i) => {
      if (p.new) {
        return this.uploadStandImage(p.file, stand.id, `Image ${i}`);
      } else {
        return Promise.resolve(p.data);
      }
    });
    stand.photos = await Promise.all(imageLoad);
    const result = this.afdb.database
      .ref(`Clients/${client}/CommercialSamples/${stand.id}`)
      .update(stand)
      .then(() => {
        return this.historyData.set(
          `Stand content`,
          "Stand",
          this.action,
          stand
        );
      })
      .then(() => {
        this.tracking.trackEntity(_TRACKING_ENTITIES.COMMERCIAL_SAMPLE, stand.id, client, stand, this.action);
        return stand;
      })
      .catch(error => {
        return error;
      });
    return result;
  }

  public async uploadStandImage(file: File, sampleId: string, name: string) {
    const task = await firebase.storage()
      .ref(`/Client/${ENV.client}/CommercialSamples/${sampleId}/${name}`)
      .put(file)
      .catch(err => {
        this.utilities.consoleDecorator('Upload error: ', err);
      });
    this.utilities.consoleDecorator('Snapshot: ', task);
    return task.downloadURL;
  }

  saveStatisticAndViewByEventId(eventId: string, sampleId: string, userId: string) {
    return this.saveStatisticByEventId(eventId, sampleId).pipe(switchMap(() => {
      return this.saveViewByEventId(eventId, sampleId, userId);
    }))
  }

  saveStatisticByEventId(eventId: string, sampleId: string) {
    return Observable.fromPromise(firebase.database().ref(`/Clients/${ENV.client}/EventStats/Commercial_Samples/${sampleId}/views`)
      .transaction(count => {
        return count += 1;
      }))
  }

  saveViewByEventId(eventId: string, sampleId: string, userId: string) {
    let id = this.afStore.createId();
    return Observable.of(this.afStore.collection(ENV.client).doc('commercial_samples_event_view').collection('event').doc(eventId).collection('views').doc(id).set({
      id: id,
      userId: userId,
      created_at: firebase.firestore.FieldValue.serverTimestamp(),
      sampleId: sampleId
    }));
  }
}


// WEBPACK FOOTER //
// ./src/providers/commercial_samples.ts